import React from 'react'
import { observer } from 'mobx-react'
import { useHistory } from 'react-router'
import Button from 'components/Button'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import Icon from 'components/Icon'
import {
  TableEmptyStateContainer,
  TableHeader,
  TableHost,
} from 'components/Table'
import { SearchableUser } from 'iam/models/SearchableUser'
import { SingleSelectFilter } from 'infra/FilterControls/SingleSelect/SingleSelectFilter'
import VirtualTable from 'payouts/components/VirtualTable'
import { UserColumn } from 'user-management/views/UserManagement/UserManagementViewModel'
import styles from './ClientUserManagementTable.sass'
import { ClientUserManagementViewModel } from 'client-user-management/views/ClientUserManagement/ClientUserManagementViewModel'
import ClientUserManagementTableControls from '../ClientUserManagementTableControls'
import ClientUserManagementTableRow from '../ClientUserManagementTableRow'
import ClientUserManagementEmptyState from '../ClientUserManagementEmptyState'

interface HeaderRowProps {
  columns: Array<UserColumn>
}

const HeaderRow = observer(({ columns }: HeaderRowProps) => (
  <>
    {columns
      .filter((col) => col.visible)
      .map((col) => (
        <TableHeader key={col.id} canSort={col.sortable} align="left">
          {col.header}
        </TableHeader>
      ))}

    <TableHeader />
  </>
))

interface TableUserCountProps<TFilters> {
  count: number
  userInactiveFilter: SingleSelectFilter<keyof TFilters, boolean>
}

const formattedCount = (count: number) => {
  return Intl.NumberFormat('en-US').format(count)
}

const TableUserCount = observer(function <
  TFilters extends Record<string, boolean>,
>({ count, userInactiveFilter }: TableUserCountProps<TFilters>) {
  const isInactiveFilterEnabled = (value: boolean) =>
    userInactiveFilter.isSelected(value)
  const changeInactiveFilterTo = (value: boolean) =>
    userInactiveFilter.change(value)

  return (
    <Dropdown
      autoDismiss
      position="bottom-start"
      button={
        <Button type="text" className={styles.userStatusDropdown}>
          {userInactiveFilter.value ? 'Inactive users' : 'Active users'}
          <span data-test="users-table-count" className={styles.resultCount}>
            ({formattedCount(count ?? 0)})
          </span>
          <Icon auto name="dropdown" />
        </Button>
      }
      dataTest="user-management-active-status-dropdown"
    >
      <DropdownTitle>Users</DropdownTitle>
      <DropdownItem
        active={isInactiveFilterEnabled(false)}
        dataTest="dropdown-active-status-option"
        checkmark={isInactiveFilterEnabled(false)}
        onClick={() => changeInactiveFilterTo(false)}
      >
        Active
      </DropdownItem>
      <DropdownItem
        active={isInactiveFilterEnabled(true)}
        dataTest="dropdown-inactive-status-option"
        checkmark={isInactiveFilterEnabled(true)}
        onClick={() => changeInactiveFilterTo(true)}
      >
        Inactive
      </DropdownItem>
    </Dropdown>
  )
})

interface ClientUserManagementTableProps {
  vm: ClientUserManagementViewModel
}

const ClientUserManagementTable = observer(
  ({ vm }: ClientUserManagementTableProps) => {
    const history = useHistory()

    const visitUserDetailsLink = (user: SearchableUser) =>
      history.push(vm.getUserDetailsLinkForUser(user))

    return (
      <div className={styles.userTable}>
        <TableUserCount
          count={vm.userSearch.totalUsers}
          userInactiveFilter={vm.userSearch.userInactiveFilter}
        />

        <TableHost>
          <ClientUserManagementTableControls vm={vm} />

          {!vm.shouldShowEmptyState ? (
            <VirtualTable
              overscan={50}
              scroller={vm.userSearch.scrollProvider}
              totalCount={vm.userSearch.loadedUsersCount}
              headerContent={() => <HeaderRow columns={vm.columns} />}
              itemContent={(index) => {
                const user = vm.users[index]
                return (
                  <ClientUserManagementTableRow
                    key={user.publicId}
                    user={user}
                  />
                )
              }}
              onClickItemRow={visitUserDetailsLink}
            />
          ) : (
            <TableEmptyStateContainer bordered>
              <ClientUserManagementEmptyState
                isSearchFilterApplied={vm.isSearchFilterApplied}
                onClearSearchFilterClick={() => vm.clearFilters()}
              />
            </TableEmptyStateContainer>
          )}
        </TableHost>
      </div>
    )
  },
)

export default ClientUserManagementTable
